/* eslint-disable no-underscore-dangle */
import { AlgoliaUtils } from '@algolia/utils';
// Get tax rate from settings or algoliaConfig
export class AlgoliaTemplate {
  static ENERGY_LABELS_MAP = {};
  static hitDataStore = new Map();
  static vendorLogoFetchCache = new Map();
  static vendorLogosTimer = null;

  /**
   * @param {unknown} value
   * @returns {string}
   */
  static normalizeSchemaText(value) {
    const stringValue = typeof value === 'string' ? value : '';
    if (!stringValue) return '';

    const element = document.createElement('div');
    element.innerHTML = stringValue;
    return (element.textContent || element.innerText || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {unknown} url
   * @returns {string}
   */
  static toAbsoluteUrl(url) {
    const urlString = typeof url === 'string' ? url : '';
    if (!urlString) return '';

    if (urlString.startsWith('//')) return `${window.location.protocol}${urlString}`;

    try {
      return new URL(urlString, window.location.origin).href;
    } catch {
      return '';
    }
  }

  static get prefix() {
    return Theme?.routes?.root_url_prefix || '/';
  }

  static getCollectionTrackingContext() {
    const dataElement = document.querySelector('#collection-products-section-data');
    if (!dataElement?.textContent) return { id: '', title: '' };

    try {
      const data = JSON.parse(dataElement.textContent);
      return {
        id: data.collection_id == null ? '' : String(data.collection_id),
        title: typeof data.collection_title === 'string' ? data.collection_title : '',
      };
    } catch (error) {
      return { id: '', title: '' };
    }
  }
  /**
   * @param {object} hit
   * @returns {string}
   */
  static getSchemaDescription(hit) {
    return AlgoliaTemplate.normalizeSchemaText(
      hit.body_html_safe || hit.meta?.custom?.kurzbeschreibung_usp || ''
    );
  }

  /**
   * Updates ItemList structured data for the currently rendered Algolia collection products.
   *
   * @param {object[]} hits
   * @param {{ page?: number, hitsPerPage?: number }} options
   * @returns {void}
   */
  static updateItemListSchema(hits, options = {}) {
    const scriptId = 'algolia-item-list-schema';
    const existingScript = document.getElementById(scriptId);
    const validHits = Array.isArray(hits) ? hits.filter((hit) => hit?.handle) : [];

    if (validHits.length === 0) {
      existingScript?.remove();
      return;
    }

    const page = Number.isInteger(options.page) ? options.page : 0;
    const hitsPerPage = Number.isInteger(options.hitsPerPage)
      ? options.hitsPerPage
      : validHits.length;
    const offset = page * hitsPerPage;
    const itemListElement = validHits.map((hit, index) => {
      const productUrl = AlgoliaTemplate.toAbsoluteUrl(`${this.prefix}products/${hit.handle}`);
      const imageUrl = AlgoliaTemplate.toAbsoluteUrl(
        hit.image || (Array.isArray(hit.images) ? hit.images[0] : '')
      );

      const item = {
        '@type': 'ListItem',
        '@id': productUrl,
        position: offset + index + 1,
        url: productUrl,
        description: AlgoliaTemplate.getSchemaDescription(hit),
      };

      if (imageUrl) item.image = imageUrl;

      return item;
    });

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement,
    };

    const script = existingScript || document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);

    if (!existingScript) {
      document.head.appendChild(script);
    }
  }

  // Create quick add button HTML with necessary data attributes for the given hit
  static createQuickAddButton(hit, horizontal = false) {
    const hitVariantId = hit.variantId || hit.objectID || hit.variants[0].id;

    const minQty = hit.meta?.custom?.mindestbestellmenge || 1;
    const step = hit.meta?.custom?.sprungmenge || 1;
    return `<quick-add-component
  class="algolia-quick-add "
  ref="quickAdd"
  data-product-title="${hit.title}"
  data-quick-add-button="add"
  data-product-options-count="${hit.options.length || 0}"
  data-min-quantity="${minQty}"
  data-increment-quantity="${step}"
>
  <product-form-component
    data-section-id="algolia-search"
    data-product-id="${hit.id}"
    data-product-url="${this.prefix}products/${hit.handle}"
    data-product-price="${hit.price}"
    ${hit.__queryID ? `data-query-id="${hit.__queryID}"` : ''}
    on:submit="/handleSubmit"
    class="quick-add__product-form-component"
  >
    <div
      class="visually-hidden"
      aria-live="assertive"
      role="status"
      aria-atomic="true"
      ref="liveRegion"
    ></div>
    <form method="post" action="/cart/add" id="QuickAdd-ProductForm-${hitVariantId}-algolia" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
    <input type="hidden" name="form_type" value="product">
    <input type="hidden" name="utf8" value="✓">
      <input
        type="hidden"
        name="id"
        ref="variantId"
        value="${hitVariantId}"
        ${hit.available === false ? 'disabled' : ''}
      >
      <input
        type="hidden"
        name="quantity"
        value="${minQty}"
      >
      <input
        type="hidden"
        name="properties[_categories]"
        value='${hit.meta.custom?.item_categories || ''}'
      >
      <input
        type="hidden"
        name="properties[_moduleClassification]"
        value="${hit.meta.custom?.pc3 || ''}"
      >
      <add-to-cart-component
  ref="addToCartButtonContainer"
  data-product-variant-media="${hit.image}"
  data-add-to-cart-animation="false"
>
  <button
    type="submit"
    name="add"
    ref="addToCartButton"
    on:click="/handleClick"
    class="button button--medium quick-add__submit-button add-to-cart-button"
  >
    <span
      class="add-to-cart-text"
    >
      <span class="add-to-cart-text__content ${!horizontal ? 'is-visually-hidden-mobile' : ''}">
        <span>
          <span>
            ${window.searchTranslations?.product?.buttons?.add_to_cart || 'In den Warenkorb'}
          </span>
        </span>
      </span>
      <span
          aria-hidden="true"
          class="svg-wrapper add-to-cart-icon"
        >
          <svg class="button__icon" aria-hidden="true" viewBox="0 0 20 20" fill="none">
            <use href="#icon-add-to-cart" fill="none"></use>
          </svg>

        </span>
    </span>
    <span class="add-to-cart__added">
      <span class="svg-wrapper add-to-cart__added-icon">
      <svg aria-hidden="true" class="checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g class="check">
    <circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <g class="burst">
    <g style="--index: 0;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 1;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 2;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 3;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 4;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 5;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 6;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
    <g style="--index: 7;">
      <line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/>
    </g>
  </g>
</svg>
      </span>
    </span>
  </button>
</add-to-cart-component>
    </form>
  </product-form-component>
</quick-add-component>`;
  }
  // New method to create badge HTML for a product hit
  static createBadgeHTML(hit) {
    if (!hit || !window.productBadges || window.productBadges.length === 0) return '';

    const isOnSale = hit.compare_at_price && hit.price < hit.compare_at_price;

    let badgesForProduct = window.productBadges.filter((badge) => {
      const hasCollections = Array.isArray(badge.collections) && badge.collections.length > 0;
      const collectionMatches =
        !hasCollections ||
        badge.collections.some((collectionId) => {
          const collectionIdNum = parseInt(collectionId, 10);
          return !isNaN(collectionIdNum) && hit.collection_ids?.includes(collectionIdNum);
        });
      const saleMatches = !badge.is_on_sale || isOnSale;

      return (
        collectionMatches &&
        saleMatches &&
        !(badge.hide_when_unavailable === true && hit.inventory_available === false)
      );
    });
    if (badgesForProduct.length === 0) return '';
    if (typeof window.badgeLimit === 'number' && window.badgeLimit > 0) {
      badgesForProduct = badgesForProduct.slice(0, window.badgeLimit);
    }
    let badgesHtml = '';
    badgesForProduct.forEach((badge) => {
      const { label, icon, color, background_color, border_color, font_family, font_weight } =
        badge;
      if (!label) return; // Skip badges without a label
      badgesHtml += `
      <div
        class="product-badges__badge product-badges__badge--rectangle product-badges__badge--custom"
        style="
          ${color ? `color: ${color};` : ''}
          ${background_color ? `background-color: ${background_color};` : ''}
          ${border_color ? `border: 1px solid ${border_color};` : ''}
          ${font_family ? `font-family: var(--font-${font_family}--family);` : ''}
          ${font_family ? `font-weight: ${font_weight};` : ''}
        ">
        ${icon ? `${icon}` : ''}
        ${label}
      </div>
    `;
    });
    if (badgesHtml) {
      badgesHtml = `<div class="product-badges product-badges__wrapper">${badgesHtml}</div>`;
    }
    return badgesHtml;
  }
  // New method to create energy label HTML
  static createEnergyLabelHTML(energyLabelData, hit) {
    if (!energyLabelData || !energyLabelData.labelMobile) return '';

    const classes =
      'energy-label energy-label--horizontal energy-label--mobile-horizontal energy-label--left energy-label--mobile-left';
    const handle = AlgoliaTemplate.escapeHtml(energyLabelData.productHandle || '');
    const labelSrc = AlgoliaTemplate.escapeHtml(energyLabelData.energyLabelExtendedUrl || '');
    const labelGid = AlgoliaTemplate.escapeHtml(energyLabelData.energyLabelGid || '');
    const specUrl = AlgoliaTemplate.escapeHtml(energyLabelData.dataSheetUrl || '');
    const specGid = AlgoliaTemplate.escapeHtml(energyLabelData.dataSheetGid || '');
    const iconSrc = AlgoliaTemplate.escapeHtml(energyLabelData.labelMobile || '');
    const ariaLabel = AlgoliaTemplate.escapeHtml(
      (window.searchTranslations?.product?.energy?.energyLabelAriaLabel || 'Energy label').replace(
        ' {{ product_title }}',
        hit.title
      )
    );
    const labelAttrs = [];
    if (labelSrc) {
      labelAttrs.push(`data-src="${labelSrc}"`);
    } else if (labelGid) {
      labelAttrs.push(`data-energy-label-gid="${labelGid}"`);
      labelAttrs.push(`data-product-handle="${handle}"`);
    }
    const energyAttrs = [];
    if (specUrl) {
      energyAttrs.push(`href="${specUrl}"`);
    } else if (specGid) {
      energyAttrs.push(`data-energy-spec-gid="${specGid}"`);
      energyAttrs.push(`data-product-handle="${handle}"`);
    }

    return `<div class="${classes}">
    <fancy-box-component class="energy-label__fancybox">
      <button
        id="energy-label-algolia-${handle}"
        ref="trigger[]"
        type="button"
        ${labelAttrs.join(' ')}
        aria-label="${ariaLabel}"
        class="energy-label__button"
      >
        <img src="${iconSrc}" alt="" width="128" height="51" loading="lazy" class="energy-label__icon energy-label__icon--left">
      </button>
    </fancy-box-component>
    ${
      energyLabelData.dataSheetUrl || energyLabelData.dataSheetGid
        ? `<a ${energyAttrs.join(' ')} target="_blank" class="energy-label__link">${
            window.searchTranslations?.product?.buttons?.dataSheet
          }</a>`
        : AlgoliaTemplate.#missingDataSheetDialog()
    }
  </div>`;
  }
  // New method to return missing data sheet dialog HTML
  static #missingDataSheetDialog() {
    return `<dialog-component
    class="popup-link">
    <button
      on:click="/showDialog"
      class="button button-unstyled gh-popup-link__button"
      type="button"
    >
      <span class="energy-label__link">${window.searchTranslations?.product?.buttons?.dataSheet}</span>
    </button>

    <dialog
      ref="dialog"
      class="popup-link__content gh-popup-link__content dialog-modal color-${window.popoverColorScheme}} popup-link__content--drawer dialog-drawer"
      scroll-lock
      aria-labelledby="popup-dialog-heading"
    >
      <header class="gh-popup-link__drawer-header">
        <div id="popup-dialog-heading" class="gh-popup-link__drawer-title h2" role="heading" aria-level="2">
          ${window.searchTranslations?.product?.buttons?.dataSheet}
        </div>
        <button
          ref="closeButton"
          on:click="/closeDialog"
          class="button button-unstyled close-button popup-link__close"
          aria-label="${window.searchTranslations?.closeDialog}"
          type="button"
        >
          <svg width="40" height="40" aria-hidden="true" class="icon" style="--icon-fill: var(--color-foreground); --icon-stroke: var(--color-foreground);">
            <use xlink:href="#icon-close-drawer"></use>
          </svg>
        </button>
      </header>

      <div class="popup-link__inner">
        <div class="product-data-missing__content">
          ${window.searchTranslations?.product?.energy?.dataSheetUnavailable}
        </div>
        <div class="product-data-missing__button">
          <a class="button button-primary button--medium button--full-width" href="${window.searchTranslations?.product?.energy?.contactLink}">${window.searchTranslations?.product?.energy?.contactNow}</a>
        </div>
      </div>
    </dialog>
  </dialog-component>`;
  }
  // Days come from the same sources as the product page. They are appended to
  // the existing status on one line.
  static getWorkDaysLabel(hit) {
    const config = window.deliveryTimeConfig || {};
    const raw = hit?.meta?.custom?.work_days;
    if (raw != null && raw !== '') {
      const value = String(raw);
      const id = value.split('/').pop();
      const mapped = config.workDaysById?.[value] || config.workDaysById?.[id];
      if (mapped) return mapped;
    }
    return config.defaultDays || '';
  }
  static getAvailabilityStatus(hit) {
    const config = window.deliveryTimeConfig || {};
    const labels = window.searchTranslations?.product?.availability || {};
    const isExpress = hit.meta?.custom?.express_tag === 1 || hit.meta?.custom?.express_tag === '1';
    const isAvailable = hit.inventory_available !== false;
    if (!isAvailable) {
      return {
        text: labels.unavailable || 'Nicht verfügbar',
        className: 'product__availability--unavailable',
      };
    }
    if (isExpress) {
      const text = labels.express || 'Sofort lieferbar';
      const days = config.enabled ? config.expressDays || '' : '';
      return {
        text: days ? `${text} ${days}` : text,
        className: 'product__availability--immediate',
      };
    }
    const text = labels.standard || 'Lieferbar';
    const days = config.enabled ? AlgoliaTemplate.getWorkDaysLabel(hit) : '';
    return {
      text: days ? `${text} ${days}` : text,
      className: 'product__availability--standard',
    };
  }
  static renderAvailability(hit, extraClass = '') {
    const status = AlgoliaTemplate.getAvailabilityStatus(hit);
    const classes = ['product__availability', status.className, extraClass]
      .filter(Boolean)
      .join(' ');
    const text = AlgoliaTemplate.escapeHtml(status.text || '');
    return `<div class="${classes}"><div class="product__availability-icon" aria-hidden="true"></div><span class="product__availability-text">${text}</span></div>`;
  }
  // Update price element template in hitTemplate
  static createPriceElement(hit, isHorizontal = false) {
    const isOnSale = hit.compare_at_price && hit.price < hit.compare_at_price;
    const mainPriceClass = isOnSale ? 'price-element__sale' : '';
    const containerClass = isHorizontal ? 'price-element__card-horizontal' : 'price-element__card';

    return `
      <div class="product-price" data-product-id="${hit.objectID}">
        <div class="${containerClass}">
          <div class="price-element__card-price">
            <!-- B2B Price (Corporate) -->
            <span role="group" class="price">
            <span class="price__amount price__amount--b2b ${mainPriceClass}"
                  aria-label="${window.searchTranslations?.product?.price?.currentPrice || 'Current price'}">
              ${AlgoliaUtils.formatPriceWithCurrency(hit.price)}
            </span>
            <!-- B2C Price (Private) -->
            <span class="price__amount price__amount--b2c ${mainPriceClass}"
                  aria-label="${window.searchTranslations?.product?.price?.currentPrice || 'Current price'}">
              ${AlgoliaUtils.formatPriceWithCurrency(hit.price * AlgoliaUtils.TAX_RATE)}
            </span>
            </span>
            
            ${
              isOnSale
                ? `
              <span role="group" class="compare-at-price">
              <!-- B2B Compare Price -->
              <s class="price__amount price__amount--b2b ${mainPriceClass}"
                    aria-label="${window.searchTranslations?.product?.price?.comparePrice || 'Compare price'}">
                ${AlgoliaUtils.formatPriceWithCurrency(hit.compare_at_price)}
              </s>
              <!-- B2C Compare Price -->
              <s class="price__amount price__amount--b2c ${mainPriceClass}"
                    aria-label="${window.searchTranslations?.product?.price?.comparePrice || 'Compare price'}">
                ${AlgoliaUtils.formatPriceWithCurrency(hit.compare_at_price * AlgoliaUtils.TAX_RATE)}
              </s>
              </span>
            `
                : ''
            }
          </div>
          <!-- B2B Tax Info -->
          <span class="price__amount--b2b tax-information">
            ${AlgoliaUtils.formatPriceWithCurrency(hit.price * AlgoliaUtils.TAX_RATE)} ${window.searchTranslations?.product?.price?.inclVat || 'inkl. MwSt.'}
          </span>
          <!-- B2C Tax Info -->
          <span class="price__amount--b2c tax-information">
            ${window.searchTranslations?.product?.price?.inclVat || 'inkl. MwSt.'}
          </span>
        </div>
      </div>
    `;
  }

  /**
   * @returns {{enabled?: boolean, freeShippingThreshold?: number, taxesIncluded?: boolean, taxRate?: number, priceSavingText?: string, labels?: Record<string, string>, hasShippingDrawer?: boolean}|null}
   */
  static getListViewConfig() {
    if (window.algoliaListViewConfig) return window.algoliaListViewConfig;

    const configEl = document.getElementById('algolia-list-view-config');
    if (!configEl) {
      window.algoliaListViewConfig = { enabled: false };
      return window.algoliaListViewConfig;
    }

    try {
      window.algoliaListViewConfig = JSON.parse(configEl.textContent || '{}');
    } catch (error) {
      AlgoliaUtils.logError(error, 'algolia list view config');
      window.algoliaListViewConfig = { enabled: false };
    }

    return window.algoliaListViewConfig;
  }

  static isAlternativeListViewEnabled() {
    const config = AlgoliaTemplate.getListViewConfig();
    if (config?.enabled) return true;
    return Boolean(document.getElementById('hits')?.dataset?.alternativeListView);
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  static handleize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  /**
   * @returns {Record<string, {logo?: string, url?: string, title?: string}>}
   */
  static getVendorLogosMap() {
    if (window.algoliaVendorLogos && typeof window.algoliaVendorLogos === 'object') {
      return window.algoliaVendorLogos;
    }

    const el = document.getElementById('algolia-vendor-logos');
    if (!el) {
      window.algoliaVendorLogos = {};
      return window.algoliaVendorLogos;
    }

    try {
      window.algoliaVendorLogos = JSON.parse(el.textContent || '{}') || {};
    } catch (error) {
      AlgoliaUtils.logError(error, 'algolia vendor logos map');
      window.algoliaVendorLogos = {};
    }

    return window.algoliaVendorLogos;
  }

  /**
   * @param {string} handle
   * @returns {string}
   */
  static getVendorLogoSectionUrl(handle) {
    const shopRoot = window.Shopify?.routes?.root || AlgoliaTemplate.prefix || '/';
    const base = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
    const url = new URL(`${base}collections/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('section_id', 'algolia-vendor-logo-data');
    return url.toString();
  }

  /**
   * @param {string} text
   * @returns {{handle?: string, logo?: string, url?: string, title?: string} | null}
   */
  static parseVendorLogoSection(text) {
    const template = document.createElement('template');
    template.innerHTML = text;
    const script = template.content.querySelector('#algolia-vendor-logo-item');
    const raw = script?.textContent || '';
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start === -1 || end === -1) return null;
    try {
      const data = JSON.parse(raw.slice(start, end + 1));
      return data?.logo ? data : null;
    } catch (error) {
      AlgoliaUtils.logError(error, 'algolia vendor logo section parse');
      return null;
    }
  }

  /**
   * @param {string} handle
   * @returns {Promise<{logo?: string, url?: string, title?: string} | null>}
   */
  static async fetchVendorLogo(handle) {
    if (AlgoliaTemplate.vendorLogoFetchCache.has(handle)) {
      return AlgoliaTemplate.vendorLogoFetchCache.get(handle);
    }

    try {
      const response = await fetch(AlgoliaTemplate.getVendorLogoSectionUrl(handle), {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        AlgoliaTemplate.vendorLogoFetchCache.set(handle, null);
        return null;
      }

      const vendorData = AlgoliaTemplate.parseVendorLogoSection(await response.text());
      AlgoliaTemplate.vendorLogoFetchCache.set(handle, vendorData);

      if (vendorData) {
        const logosMap = AlgoliaTemplate.getVendorLogosMap();
        logosMap[handle] = vendorData;
        if (vendorData.handle && vendorData.handle !== handle) {
          logosMap[vendorData.handle] = vendorData;
        }
      }

      return vendorData;
    } catch (error) {
      AlgoliaUtils.logError(error, 'algolia vendor logo section fetch');
      AlgoliaTemplate.vendorLogoFetchCache.set(handle, null);
      return null;
    }
  }

  /**
   * @returns {void}
   */
  static loadVendorLogosMap() {
    if (!document.getElementById('algolia-vendor-logos')) return;

    if (AlgoliaTemplate.vendorLogosTimer) {
      clearTimeout(AlgoliaTemplate.vendorLogosTimer);
    }

    AlgoliaTemplate.vendorLogosTimer = setTimeout(async () => {
      AlgoliaTemplate.vendorLogosTimer = null;
      const logosMap = AlgoliaTemplate.getVendorLogosMap();
      const pendingHandles = new Set();

      document.querySelectorAll('[data-vendor-handle]').forEach((el) => {
        const handle = el.getAttribute('data-vendor-handle');
        if (!handle || el.querySelector('img.product-vendor__image')) return;
        if (logosMap[handle]?.logo) return;
        pendingHandles.add(handle);
      });

      if (pendingHandles.size > 0) {
        await Promise.all(
          [...pendingHandles].map((handle) => AlgoliaTemplate.fetchVendorLogo(handle))
        );
      }

      AlgoliaTemplate.applyVendorLogosToDOM();
    }, 50);
  }

  /**
   * @returns {void}
   */
  static applyVendorLogosToDOM() {
    const logosMap = AlgoliaTemplate.getVendorLogosMap();
    document.querySelectorAll('[data-vendor-handle]').forEach((el) => {
      const vendorHandle = el.getAttribute('data-vendor-handle');
      if (!vendorHandle || el.querySelector('img.product-vendor__image')) return;

      const vendorData = logosMap[vendorHandle];
      if (!vendorData?.logo) return;

      const vendorLabel = el.getAttribute('data-vendor-label') || vendorData.title || vendorHandle;
      el.innerHTML = AlgoliaTemplate.renderVendorInnerHTML(vendorLabel, vendorData);
    });
  }

  /**
   * @param {string} vendorLabel
   * @param {{logo?: string, url?: string, title?: string} | null} vendorData
   * @returns {string}
   */
  static renderVendorInnerHTML(vendorLabel, vendorData) {
    const safeLabel = AlgoliaTemplate.escapeHtml(vendorLabel);
    const collectionUrl = vendorData?.url || '';
    const logoUrl = vendorData?.logo || '';
    const ariaLabel = AlgoliaTemplate.escapeHtml(vendorData?.title || vendorLabel);

    if (logoUrl && collectionUrl) {
      return `<a
          href="${AlgoliaTemplate.escapeHtml(collectionUrl)}"
          class="product-vendor__link"
          aria-label="${ariaLabel}"
          onclick="event.stopPropagation()"
        >
          <img
            src="${AlgoliaTemplate.escapeHtml(logoUrl)}"
            alt="${safeLabel}"
            class="product-vendor__image product-card-horizontal-alt__vendor-image"
            loading="lazy"
            width="150"
            height="40"
          />
        </a>`;
    }

    if (collectionUrl) {
      return `<a
          href="${AlgoliaTemplate.escapeHtml(collectionUrl)}"
          class="product-vendor__link"
          aria-label="${ariaLabel}"
          onclick="event.stopPropagation()"
        >
          ${safeLabel}
        </a>`;
    }

    return `<span class="product-vendor__link product-card-horizontal-alt__vendor-text">${safeLabel}</span>`;
  }

  /**
   * @param {object} hit
   * @returns {string}
   */
  static createVendorImageHTML(hit) {
    const vendorLabel = hit.vendor || '';
    if (!vendorLabel) return '';

    AlgoliaTemplate.loadVendorLogosMap();

    const vendorHandle = AlgoliaTemplate.handleize(vendorLabel);
    const logosMap = AlgoliaTemplate.getVendorLogosMap();
    const vendorData = vendorHandle ? logosMap[vendorHandle] : null;

    return `<div
        class="product-card-horizontal-alt__vendor product-vendor"
        data-vendor-handle="${AlgoliaTemplate.escapeHtml(vendorHandle)}"
        data-vendor-label="${AlgoliaTemplate.escapeHtml(vendorLabel)}"
      >${AlgoliaTemplate.renderVendorInnerHTML(vendorLabel, vendorData)}</div>`;
  }

  /**
   * @param {object} hit
   * @returns {number|null}
   */
  static getRecommendedRetailPrice(hit) {
    const raw = hit?.meta?.custom?.recommended_retail_price;
    if (raw == null || raw === '') return null;

    let value =
      typeof raw === 'object'
        ? parseFloat(raw.amount ?? raw.value ?? raw.price ?? NaN)
        : parseFloat(raw);

    if (!Number.isFinite(value) || value <= 0) return null;

    // Money metafields may be indexed in cents; Algolia product prices are major units.
    if (hit.price && value > hit.price * 20) {
      value /= 100;
    }

    return value;
  }

  /**
   * @param {object} hit
   * @returns {string}
   */
  static createReviewsHTML(hit, { hidden = true } = {}) {
    if (!(hit.meta?.reviews?.rating && hit.meta?.reviews?.rating_count)) return '';

    let ratingValue = 0;
    try {
      const rating = hit.meta.reviews.rating;
      if (typeof rating === 'string') {
        const parsed = JSON.parse(rating);
        ratingValue = parseFloat(parsed?.value ?? parsed);
      } else if (typeof rating === 'object' && rating !== null) {
        ratingValue = parseFloat(rating.value);
      } else {
        ratingValue = parseFloat(rating);
      }
    } catch {
      ratingValue = 0;
    }

    if (!Number.isFinite(ratingValue) || ratingValue <= 0) return '';

    const fullStars = Math.floor(ratingValue);
    const hasHalfStar = ratingValue % 1 >= 0.5;
    const emptyStars = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));

    let starsHTML = Array(fullStars).fill('<span class="jdgm-star jdgm--on"></span>').join('');
    if (hasHalfStar) starsHTML += '<span class="jdgm-star jdgm--half"></span>';
    starsHTML += Array(emptyStars).fill('<span class="jdgm-star jdgm--off"></span>').join('');

    return `
      <div
        ${hidden ? 'style="display:none"' : ''}
        class="jdgm-prev-badge"
        data-average-rating="${ratingValue}"
        data-number-of-reviews="${hit.meta.reviews.rating_count}"
      >
        <span
          class="jdgm-prev-badge__stars"
          data-score="${ratingValue}"
          tabindex="0"
          aria-label="${ratingValue} stars"
          role="button"
        >
          ${starsHTML}
        </span>
        <span class="jdgm-prev-badge__text"> (${hit.meta.reviews.rating_count})</span>
      </div>
    `;
  }

  /**
   * @param {object} hit
   * @returns {string}
   */
  static createListAltPriceElement(hit) {
    const config = AlgoliaTemplate.getListViewConfig() || {};
    const labels = config.labels || {};
    const uvp = AlgoliaTemplate.getRecommendedRetailPrice(hit);
    const compareAt = Number(hit.compare_at_price) || 0;
    const price = Number(hit.price) || 0;

    let referencePrice = null;
    let referenceSource = null;

    if (uvp != null && uvp > price) {
      referencePrice = uvp;
      referenceSource = 'uvp';
    } else if (compareAt > price) {
      referencePrice = compareAt;
      referenceSource = 'compare_at';
    }

    const showCompare = referencePrice != null;
    const saving = showCompare ? referencePrice - price : 0;

    const referenceLabel =
      referenceSource === 'uvp'
        ? labels.recommendedRetailPrice || 'UVP²:'
        : labels.regularPrice || 'Regulärer Preis:';
    const currentLabel =
      referenceSource === 'uvp'
        ? labels.priceColon || 'Preis:'
        : labels.specialOffer || 'Sonderangebot:';

    const priceRow = (label, amountHtml, extraClass = '') => `
      <div class="product-card-horizontal-alt__price-row ${extraClass}">
        <span class="product-card-horizontal-alt__price-label">${label}</span>
        <span class="product-card-horizontal-alt__price-value">${amountHtml}</span>
      </div>
    `;

    const dualAmount = (amount, { strike = false, strong = false, sale = false } = {}) => {
      const tagOpen = strike ? '<s>' : strong ? '<strong>' : '';
      const tagClose = strike ? '</s>' : strong ? '</strong>' : '';
      const saleClass = sale ? ' product-card-horizontal-alt__price-value--sale' : '';
      return `
        <span class="price__amount price__amount--b2b${saleClass}">${tagOpen}${AlgoliaUtils.formatPriceWithCurrency(amount)}${tagClose}</span>
        <span class="price__amount price__amount--b2c${saleClass}">${tagOpen}${AlgoliaUtils.formatPriceWithCurrency(amount * AlgoliaUtils.TAX_RATE)}${tagClose}</span>
      `;
    };

    const shippingOpener = AlgoliaTemplate.createListShippingOpener(hit);

    return `
      <div class="product-price product-card-horizontal-alt__price" data-product-id="${hit.objectID}">
        ${
          showCompare
            ? priceRow(
                referenceLabel,
                dualAmount(referencePrice, { strike: true }),
                'product-card-horizontal-alt__price-row--compare'
              )
            : ''
        }
        ${priceRow(
          showCompare ? currentLabel : labels.priceColon || 'Preis:',
          dualAmount(price, { strong: true, sale: showCompare }),
          showCompare
            ? 'product-card-horizontal-alt__price-row--current product-card-horizontal-alt__price-row--sale'
            : 'product-card-horizontal-alt__price-row--current'
        )}
        ${
          showCompare && saving > 0
            ? priceRow(
                `${config.priceSavingText || 'Sie sparen'}:`,
                dualAmount(saving),
                'product-card-horizontal-alt__price-row--saving'
              )
            : ''
        }
        <div class="product-card-horizontal-alt__tax-shipping">
          <span class="price__amount--b2b tax-information">
            ${labels.inclVat || 'inkl. MwSt.'}
            ${AlgoliaUtils.formatPriceWithCurrency(price * AlgoliaUtils.TAX_RATE)}
          </span>
          <span class="price__amount--b2c tax-information">
            ${labels.inclVat || 'inkl. MwSt.'}
          </span>
          ${shippingOpener}
        </div>
      </div>
    `;
  }

  /**
   * @param {object} hit
   * @returns {string}
   */
  static createListShippingOpener(hit) {
    const config = AlgoliaTemplate.getListViewConfig();
    if (!config?.hasShippingDrawer) return '';

    const labels = config.labels || {};
    const threshold = Number(config.freeShippingThreshold) || 0;
    let priceNet = Number(hit.price) || 0;
    const taxMultiplier = 1 + (Number(config.taxRate) || 19) / 100;

    if (config.taxesIncluded) {
      priceNet /= taxMultiplier;
    }

    const thresholdNet = threshold / 100;
    const isFreeShipping = thresholdNet > 0 && priceNet >= thresholdNet;
    const openerText = isFreeShipping
      ? labels.freeShipping || 'Versandkostenfrei'
      : labels.shippingIncl || 'zzgl. Versandkosten';

    return `
      <button
        type="button"
        class="button-unstyled link product-card-horizontal-alt__shipping-opener"
        onclick="AlgoliaTemplate.openListShippingDrawer(event)"
      >
        ${openerText}
      </button>
    `;
  }

  static openListShippingDrawer(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const drawer = document.getElementById('algolia-list-shipping-drawer');
    if (!drawer) return;

    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      return;
    }

    const dialog = drawer.querySelector('dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  }

  static getProductCategories(hit) {
    let categories = [];
    try {
      if (hit.meta?.custom?.item_categories) {
        let categoriesData = hit.meta.custom.item_categories;
        if (typeof categoriesData === 'string') {
          try {
            categoriesData = JSON.parse(categoriesData);
          } catch (error) {
            AlgoliaUtils.logError('Failed to parse categories JSON:', error);
          }
        }
        if (
          Array.isArray(categoriesData) &&
          categoriesData.length === 1 &&
          typeof categoriesData[0] === 'string'
        ) {
          categories = categoriesData[0].split('/').map((cat) => cat.trim());
        } else if (typeof categoriesData === 'string' && categoriesData.includes('/')) {
          categories = categoriesData.split('/').map((cat) => cat.trim());
        } else if (Array.isArray(categoriesData)) {
          categories = categoriesData;
        }
        categories = categories.map((cat) => {
          if (typeof cat === 'string') {
            return cat.replace(/[[\]"\\]/g, '').trim();
          }
          return cat;
        });
      }
    } catch (error) {
      AlgoliaUtils.logError('Error parsing categories:', error);
      categories = [];
    }

    return categories;
  }

  static handleProductClick(productHandle, e) {
    const isInteractiveElement = e
      .composedPath()
      .some(
        (element) =>
          element instanceof Element &&
          element.matches(
            'button, input, label, select, [tabindex="1"], .wishlist, .wishlist-button-wrapper, fancy-box-component, .energy-label__link, dialog-component, quick-add-component, .algolia-alternative-product, .product-card-horizontal-alt__shipping-opener'
          )
      );

    if (isInteractiveElement) {
      return;
    }
    const hit = AlgoliaTemplate.hitDataStore.get(productHandle);
    if (!hit) return;

    e.preventDefault();
    e.stopPropagation();

    const categories = AlgoliaTemplate.getProductCategories(hit);

    // Construct ecommerce object
    const ecommerce = {
      items: [
        {
          item_id: hit.objectID,
          item_sku: hit.sku || '',
          item_variant: hit.objectID,
          item_name: hit.title,
          variant_name: hit.title,
          item_category: categories[0] || '',
          item_category2: categories[1] || '',
          item_category3: categories[2] || '',
          price: hit.price * AlgoliaUtils.TAX_RATE,
          item_brand: hit.vendor,
          quantity: '1',
        },
      ],
    };

    // Prepare select_item_dev data
    const tempSelectDev = {
      customer_type_g: window.selectedCustomerType === 'private' ? 'B2C' : 'B2B',
      country_language_g: window.country_language_g || null,
      login_status_g: !!window.customer,
      uuid_g: window.customer?.id || null,
      checkout_type: window.customer ? 'registered' : 'guest',
      newsletter_status: window.customer?.accepts_marketing ? 'subscribed' : 'not_subscribed',
      items: [
        {
          item_id: ecommerce.items[0].item_id,
          item_category: ecommerce.items[0].item_category,
          item_category2: ecommerce.items[0].item_category2,
          item_category3: ecommerce.items[0].item_category3,
        },
      ],
    };
    const navigateUrl = `${this.prefix}products/${hit.handle}${hit.__queryID ? `?queryID=${hit.__queryID}` : ''}`;
    const navigate = () => {
      window.location.href = navigateUrl;
    };
    setTimeout(navigate, 1000);
    if (window.aa) {
      window.aa('clickedObjectIDsAfterSearch', {
        eventName: 'Hit Clicked',
        index: window.algoliaConfig?.indexName || 'shopify_products',
        objectIDs: [hit.objectID],
        positions: [hit.position || 0],
        queryID: hit.__queryID || '',
      });
    }

    // Push to dataLayer — eventCallback navigates after GTM has fired all tags,
    // eventTimeout is the fallback in case GTM doesn't respond within 1s.
    if (typeof window.clearPreviousDataLayerItems === 'function') {
      window.clearPreviousDataLayerItems();
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null, items: null });
    }
    window.dataLayer.push(tempSelectDev);
    window.dataLayer.push({
      event: 'select_item_dev',
      user_data: AlgoliaDataUtils.getCustomerData(),
      ecommerce,
      ecomm_pagetype: window.template?.name || '',
      eventCallback: navigate,
      eventTimeout: 1000,
    });
  }

  static initBadgeMap() {
    try {
      const energyLabelData = document.getElementById('energy-label-data');
      if (energyLabelData) {
        const map = JSON.parse(energyLabelData.textContent).energy_labels || {};
        Object.values(map).forEach((label) => {
          const classKey = String(label.energy_label || '')
            .trim()
            .split(/\s+/)
            .pop();
          if (classKey) map[classKey] = label;
        });
        AlgoliaTemplate.ENERGY_LABELS_MAP = map;
      }
    } catch (error) {
      AlgoliaUtils.logError('Failed to initialize badges map:', error);
      AlgoliaTemplate.ENERGY_LABELS_MAP = {};
    }
  }

  static errorTemplate(
    message = window.searchTranslations?.error?.default?.message,
    subtitle = window.searchTranslations?.error?.default?.subtitle,
    pageType = 'default',
    hasActiveFilters = false
  ) {
    // Determine the appropriate messages based on page type and filter state
    let finalMessage = message;
    let finalSubtitle = subtitle;
    let buttonText = window.searchTranslations?.filters?.resetAll || 'Alle zurücksetzen';
    let buttonAction = 'location.reload()';
    let showButton = true;
    if (pageType === 'search') {
      if (hasActiveFilters) {
        // Search page with active filters
        finalMessage = window.searchTranslations?.error?.search?.message;
        finalSubtitle = window.searchTranslations?.error?.search?.subtitle;
        buttonText = window.searchTranslations?.filters?.resetAll;
        buttonAction = `window.__instantSearchInstance && window.__instantSearchInstance.setUiState({ ${window.algoliaConfig?.indexName}: { query: new URLSearchParams(window.location.search).get('q') || '' } })`;
        showButton = true;
      } else {
        // Search page without filters (original behavior)
        finalMessage = window.searchTranslations?.error?.default?.message;
        finalSubtitle = window.searchTranslations?.error?.default?.subtitle;
        showButton = false;
      }
    } else if (pageType === 'collection') {
      if (hasActiveFilters) {
        // Collection page with active filters
        finalMessage = window.searchTranslations?.error?.collection?.filter?.message;
        finalSubtitle = window.searchTranslations?.error?.collection?.filter?.subtitle;
        buttonText = window.searchTranslations?.filters?.resetAll;
        buttonAction = `window.__instantSearchInstance && window.__instantSearchInstance.setUiState({ ${window.algoliaConfig?.indexName}: {} })`;
        showButton = true;
      } else {
        // Collection page without filters (original behavior)
        finalMessage = window.searchTranslations?.error?.collection?.message;
        finalSubtitle = window.searchTranslations?.error?.collection?.subtitle;
        buttonText = window.searchTranslations?.error?.collection?.buttonText;
        buttonAction = `window.location.href = '${window.algoliaConfig?.collection_error_redirect_url || '/'}'`;
        showButton = true;
      }
    } else if (hasActiveFilters) {
      // Default page type with active filters
      finalMessage =
        window.searchTranslations?.error?.noProductsFound || 'Leider keine Produkte gefunden!';
      finalSubtitle =
        window.searchTranslations?.error?.tryFewerFilters ||
        'Verwende weniger Filter oder alle zurücksetzen';
      buttonText = window.searchTranslations?.filters?.resetAll || 'Alle zurücksetzen';
      buttonAction = `window.__instantSearchInstance && window.__instantSearchInstance.setUiState({ ${window.algoliaConfig?.indexName}: {} })`;
      showButton = true;
    }

    return `
      <div class="results-empty page-width">
        <div class="results-empty-content">
          <svg aria-hidden="false" class="results-empty-notify" style="fill: none;">
            <use href="#icon-bell-notification"></use>
          </svg>
          <span class="text--body text--body-bold text--body-md text--primary">
            ${finalMessage}
          </span>
          <span class="text--body text--body-md text--primary">
            ${finalSubtitle}
          </span>
        </div>
        ${
          showButton
            ? `
          <button onclick="${buttonAction}" class="button button-secondary button--medium results-empty-button">
            ${buttonText}
          </button>
        `
            : ''
        }
      </div>
    `;
  }

  static hitTemplate(hit, titleTag = 'span') {
    try {
      if (!hit || !hit.handle || !hit.title) {
        AlgoliaUtils.logError(new Error('Invalid hit data'), 'hitTemplate');
        return '';
      }
      // PP-1011: warranty add-ons are sold only together with a product, never as a card.
      if (hit.template_suffix === 'warranty') return '';
      AlgoliaTemplate.hitDataStore.set(hit.handle, hit);
      const validTitleTag = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p'].includes(titleTag)
        ? titleTag
        : 'span';
      const imgSrc = hit.image || (hit.images ? hit.images[0] : window.theme?.assets?.placeholder);
      const energyLabelData = AlgoliaDataUtils.getEnergyLabelData(hit);
      const collectionTrackingContext = AlgoliaTemplate.getCollectionTrackingContext();
      return `
          <article class="product-card algolia-product-card" data-product-handle="${hit.handle}" onclick="AlgoliaTemplate.handleProductClick('${hit.handle}', event)">
          <a class="product-card__link" href="${this.prefix}products/${hit.handle}${hit.__queryID ? `?queryID=${hit.__queryID}` : ''}" aria-label="${hit.title}"></a>
            <div class="product-card__header"
                data-collection-title="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.title)}"
                data-collection-id="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.id)}">
                <img
                  src="${imgSrc}"
                  alt="${hit.title}"
                  class="product-card__img image__img image--contain"
                  loading="lazy"
                  onerror="this.src='${window.theme.assets.placeholder}'"
                />

              ${AlgoliaTemplate.createBadgeHTML(hit)}
              <div class="product-card__atc">
                ${hit.inventory_available !== false ? AlgoliaTemplate.createQuickAddButton(hit) : ''}
              </div>
              <div onclick="this.dispatchEvent(new CustomEvent('wishlist_click', { bubbles: true }))" class="product-card__like-btn product-card__like-btn--horizon wishlist wishlist--icon" data-product-handle="${hit.handle}">
                <div class="wishlist-engine" data-product_id="${hit.id}" data-variant_id="${hit.variantId || hit.objectID || hit.variants?.[0]?.id || ''}" data-full_button="false" data-css="false">
                </div>
              </div>
            </div>
            <div class="product-card__body">
              <div class="product-card__info">
                <span class="product-card__vendor">
                  ${hit.vendor || window.searchTranslations?.product?.unknownBrand || 'Unknown Brand'}
                </span>
                <${validTitleTag} class="product-card__title">
                  ${hit.title}
                </${validTitleTag}>
              </div>
              ${energyLabelData ? AlgoliaTemplate.createEnergyLabelHTML(energyLabelData, hit) : ''}
${
  hit.meta?.reviews?.rating && hit.meta?.reviews?.rating_count
    ? `
<div
  style="display:none; margin-top: 12px;"
  class="jdgm-prev-badge"
  data-average-rating="${JSON.parse(hit.meta.reviews.rating).value}"
  data-number-of-reviews="${hit.meta.reviews.rating_count}"
>
  <span
    class="jdgm-prev-badge__stars"
    data-score="${JSON.parse(hit.meta.reviews.rating).value}"
    tabindex="0"
    aria-label="${JSON.parse(hit.meta.reviews.rating).value} stars"
    role="button"
  >
    ${(() => {
      const rating = parseFloat(JSON.parse(hit.meta.reviews.rating).value);
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating % 1 >= 0.5;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

      let starsHTML = '';

      // Add full stars
      starsHTML += Array(fullStars).fill('<span class="jdgm-star jdgm--on"></span>').join('');

      // Add half star if needed
      if (hasHalfStar) {
        starsHTML += '<span class="jdgm-star jdgm--half"></span>';
      }

      // Add empty stars
      starsHTML += Array(emptyStars).fill('<span class="jdgm-star jdgm--off"></span>').join('');

      return starsHTML;
    })()}
  </span>
  <span class="jdgm-prev-badge__text"> (${hit.meta.reviews.rating_count})</span>
</div>
`
    : ''
}
              ${AlgoliaTemplate.createPriceElement(hit)}
              ${AlgoliaTemplate.renderAvailability(hit)}
            </div>
          </article>
        `;
    } catch (error) {
      AlgoliaUtils.logError(error, 'hitTemplate rendering');
      return '';
    }
  }

  static hitTemplateList(hit, titleTag = 'span') {
    try {
      if (!hit || !hit.handle || !hit.title) {
        AlgoliaUtils.logError(new Error('Invalid hit data'), 'hitTemplateList');
        return '';
      }
      // PP-1011: warranty add-ons are sold only together with a product, never as a card.
      if (hit.template_suffix === 'warranty') return '';
      // Store hit data for click handling
      AlgoliaTemplate.hitDataStore.set(hit.handle, hit);
      const validTitleTag = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p'].includes(titleTag)
        ? titleTag
        : 'span';
      const imgSrc = hit.image || (hit.images ? hit.images[0] : window.theme?.assets?.placeholder);
      // Extract description bullets if available
      const descriptionHTML = hit.meta?.custom?.kurzbeschreibung_usp
        ? `
        <div class="product-card-horizontal__description">
          ${hit.meta?.custom?.kurzbeschreibung_usp}
        </div>
      `
        : '';
      const energyLabelData = AlgoliaDataUtils.getEnergyLabelData(hit);
      const collectionTrackingContext = AlgoliaTemplate.getCollectionTrackingContext();
      return `
        <div class="products-list__item">
          <div class="product-card-horizontal" data-product-handle="${hit.handle}" onclick="AlgoliaTemplate.handleProductClick('${hit.handle}', event)">
            <div class="product-card-horizontal__header"
                data-collection-title="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.title)}"
                data-collection-id="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.id)}">
              <a href="${this.prefix}products/${hit.handle}${hit.__queryID ? `?queryID=${hit.__queryID}` : ''}"
                      class="image product-card-horizontal__img"
                      style="--object-fit: contain"
                      product-id="${hit.objectID}">
                      <img
                        src="${imgSrc}"
                        alt="${hit.title}"
                        class="image__img image--contain"
                        loading="lazy"
                        onerror="this.src='${window.theme?.assets?.placeholder}'"
                      />
                    </a>
              <div class="product-card-horizontal__buttons">
              <div onclick="this.dispatchEvent(new CustomEvent('wishlist_click', { bubbles: true }))" class="product-card-horizontal__like-btn button button-unstyled wishlist wishlist--icon" data-product-handle="${hit.handle}">
                <div class="wishlist-engine" data-product_id="${hit.id}" data-variant_id="${hit.variantId || hit.objectID || hit.variants?.[0]?.id || ''}" data-full_button="false" data-css="false">
                </div>
              </div>
                
              </div>
            </div>
            <a href="${this.prefix}products/${hit.handle}${hit.__queryID ? `?queryID=${hit.__queryID}` : ''}" class="product-card-horizontal__body">
              <div class="product-card-horizontal__info">
                ${AlgoliaTemplate.createBadgeHTML(hit)}
                <span class="h6 product-card-horizontal__vendor">
                  ${hit.vendor || window.searchTranslations?.product?.unknownBrand || 'Unknown Brand'}
                </span>
                <${validTitleTag} class="product-card-horizontal__title">
                  ${hit.title}
                </${validTitleTag}>
                ${
                  hit.meta?.reviews?.rating && hit.meta?.reviews?.rating_count
                    ? `
                <div
                  style="display:none"
                  class="jdgm-prev-badge"
                  data-average-rating="${JSON.parse(hit.meta.reviews.rating).value}"
                  data-number-of-reviews="${hit.meta.reviews.rating_count}"
                >
                  <span
                    class="jdgm-prev-badge__stars"
                    data-score="${JSON.parse(hit.meta.reviews.rating).value}"
                    tabindex="0"
                    aria-label="${JSON.parse(hit.meta.reviews.rating).value} stars"
                    role="button"
                  >
                    ${(() => {
                      const rating = parseFloat(JSON.parse(hit.meta.reviews.rating).value);
                      const fullStars = Math.floor(rating);
                      const hasHalfStar = rating % 1 >= 0.5;
                      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

                      let starsHTML = '';

                      // Add full stars
                      starsHTML += Array(fullStars)
                        .fill('<span class="jdgm-star jdgm--on"></span>')
                        .join('');

                      // Add half star if needed
                      if (hasHalfStar) {
                        starsHTML += '<span class="jdgm-star jdgm--half"></span>';
                      }

                      // Add empty stars
                      starsHTML += Array(emptyStars)
                        .fill('<span class="jdgm-star jdgm--off"></span>')
                        .join('');

                      return starsHTML;
                    })()}
                  </span>
                  <span class="jdgm-prev-badge__text"> (${hit.meta.reviews.rating_count})</span>
                </div>
                `
                    : ''
                }
              </div>
              ${descriptionHTML}
            </a>
            <div class="product-card-horizontal__footer">
              ${energyLabelData ? AlgoliaTemplate.createEnergyLabelHTML(energyLabelData, hit) : ''}
              ${AlgoliaTemplate.createPriceElement(hit, true)}
              ${AlgoliaTemplate.renderAvailability(hit)}
              ${hit.inventory_available !== false ? AlgoliaTemplate.createQuickAddButton(hit, true) : ''}
            </div>
            ${window.algoliaAlternativeProductConfig && window.algoliaAlternativeProductConfig?.enabled ? AlgoliaTemplate.createAlternativeProductHTML(hit) : ''}
          </div>
        </div>
        `;
    } catch (error) {
      AlgoliaUtils.logError(error, 'hitTemplateList rendering');
      return '';
    }
  }

  /**
   * @param {object} hit
   * @param {string} [titleTag]
   * @returns {string}
   */
  static hitTemplateListAlt(hit, titleTag = 'span') {
    try {
      if (!hit || !hit.handle || !hit.title) {
        AlgoliaUtils.logError(new Error('Invalid hit data'), 'hitTemplateListAlt');
        return '';
      }
      // PP-1011: warranty add-ons are sold only together with a product, never as a card.
      if (hit.template_suffix === 'warranty') return '';

      AlgoliaTemplate.hitDataStore.set(hit.handle, hit);
      const config = AlgoliaTemplate.getListViewConfig() || {};
      const labels = config.labels || {};
      const validTitleTag = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p'].includes(titleTag)
        ? titleTag
        : 'span';
      const imgSrc = hit.image || (hit.images ? hit.images[0] : window.theme?.assets?.placeholder);
      const sku = hit.sku || hit.variants?.[0]?.sku || '';
      const energyLabelData = AlgoliaDataUtils.getEnergyLabelData(hit);
      const productUrl = `${this.prefix}products/${hit.handle}${hit.__queryID ? `?queryID=${hit.__queryID}` : ''}`;
      const collectionTrackingContext = AlgoliaTemplate.getCollectionTrackingContext();

      const descriptionHTML = hit.meta?.custom?.kurzbeschreibung_usp
        ? `<div class="product-card-horizontal-alt__usps">${hit.meta.custom.kurzbeschreibung_usp}</div>`
        : '';

      const vendorHTML = AlgoliaTemplate.createVendorImageHTML(hit);

      const skuHTML = sku
        ? `<div class="product-card-horizontal-alt__sku">
            <span>${labels.sku || 'Art.-Nr.:'}</span>
            <span>${sku}</span>
          </div>`
        : '';

      return `
        <div class="products-list__item products-list__item--alt">
          <article
            class="product-card-horizontal product-card-horizontal--alt"
            data-product-handle="${hit.handle}"
            data-collection-id="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.id)}"
            data-collection-title="${AlgoliaTemplate.escapeHtml(collectionTrackingContext.title)}"
            onclick="AlgoliaTemplate.handleProductClick('${hit.handle}', event)"
          >
          <div class="product-card-horizontal-alt__media">
              <div class="product-card-horizontal-alt__buttons">
                <div onclick="this.dispatchEvent(new CustomEvent('wishlist_click', { bubbles: true }))" class="product-card-horizontal__like-btn button button-unstyled wishlist wishlist--icon" data-product-handle="${hit.handle}">
                  <div class="wishlist-engine" data-product_id="${hit.id}" data-variant_id="${hit.variantId || hit.objectID || hit.variants?.[0]?.id || ''}" data-full_button="false" data-css="false">
                  </div>
                </div>
              </div>
              ${AlgoliaTemplate.createBadgeHTML(hit)}
              <a
                href="${productUrl}"
                class="image product-card-horizontal-alt__image"
                style="--object-fit: contain"
                product-id="${hit.objectID}"
                aria-label="${hit.title}"
              >
                <img
                  src="${imgSrc}"
                  alt="${hit.title}"
                  class="image__img image--contain"
                  loading="lazy"
                  onerror="this.src='${window.theme?.assets?.placeholder}'"
                />
              </a>
            </div>

            <div class="product-card-horizontal-alt__content">
              ${vendorHTML}
              <${validTitleTag} class="product-card-horizontal-alt__title">
                <a href="${productUrl}">${hit.title}</a>
              </${validTitleTag}>
              ${skuHTML}
              ${AlgoliaTemplate.createReviewsHTML(hit, { hidden: false })}
              ${descriptionHTML}
              ${AlgoliaTemplate.renderAvailability(hit, 'product-card-horizontal-alt__availability')}
            </div>

            <div class="product-card-horizontal-alt__aside">
              ${energyLabelData ? AlgoliaTemplate.createEnergyLabelHTML(energyLabelData, hit) : ''}
              ${AlgoliaTemplate.createListAltPriceElement(hit)}
              ${
                hit.inventory_available !== false
                  ? `<div class="product-card-horizontal-alt__atc">${AlgoliaTemplate.createQuickAddButton(hit, true)}</div>`
                  : ''
              }
            </div>

            ${
              window.algoliaAlternativeProductConfig?.enabled
                ? AlgoliaTemplate.createAlternativeProductHTML(hit)
                : ''
            }
          </article>
        </div>
      `;
    } catch (error) {
      AlgoliaUtils.logError(error, 'hitTemplateListAlt rendering');
      return '';
    }
  }

  static clearHitDataStore() {
    AlgoliaTemplate.hitDataStore.clear();
  }

  static createAlternativeProductHTML(hit) {
    const config = window.algoliaAlternativeProductConfig;
    if (!config || !config.alternativeProductField || !hit.meta) {
      return '';
    }

    // fieldToCheck/alternativeProductField are dotted paths (e.g. "custom.field")
    // that need to be resolved against the nested hit.meta JSON structure.
    /**
     * @param {string} path
     * @returns {any}
     */
    const getMetaValue = (path) =>
      path.split('.').reduce(
        /**
         * @param {any} value
         * @param {string} key
         */
        (value, key) => (value == null ? undefined : value[key]),
        hit.meta
      );

    // Optional metafield gate: only continue when the configured field matches valueToCheck.
    if (config.checkField && config.fieldToCheck && config.valueToCheck) {
      const fieldValue = getMetaValue(config.fieldToCheck);
      if (fieldValue != null && String(fieldValue) !== String(config.valueToCheck)) {
        return '';
      }
    }
    // Availability gates (AND), mirroring product-alternative.liquid. The two flags are now
    // two INDEPENDENT gates that are AND-combined: each enabled flag adds a condition that
    // must be met, while a disabled flag is ignored (its gate always passes).
    //   - showWhenUnavailable: when enabled, requires the product to be unavailable
    //     (inventory_available === false).
    //   - showWhenInventoryContinue: when enabled, requires overselling to be allowed
    //     (inventory policy = continue).
    // Enabling both means BOTH conditions must hold for the alternative to show.
    let availabilityMatch = false;
    if (
      config.showWhenUnavailable === true &&
      (hit.inventory_available === false || hit.inventory_quantity <= 0)
    ) {
      availabilityMatch = true;
    } else if (config.showWhenUnavailable !== true) {
      availabilityMatch = true;
    }

    let policyMatch = false;
    if (config.showWhenInventoryContinue === true && hit.inventory_policy === 'continue') {
      policyMatch = true;
    } else if (config.showWhenInventoryContinue !== true) {
      policyMatch = true;
    }

    if (!availabilityMatch || !policyMatch) {
      return '';
    }

    const alternativeProduct = getMetaValue(config.alternativeProductField);
    const alternativeGids = AlgoliaDataUtils.parseGidList(alternativeProduct);
    if (alternativeGids.length === 0) {
      return '';
    }

    // Defer resolution until the placeholder is in the DOM. resolveAlternativeProductGids
    // is debounced and scans the DOM, so all alternative placeholders rendered in the same
    // batch collapse into a single Storefront API request.
    setTimeout(() => {
      AlgoliaDataUtils.resolveAlternativeProductGids?.();
    }, 0);

    return `
      <div class="algolia-alternative-product is-loading" data-alternative-product-gids="${alternativeGids.join(
        ','
      )}">
        <span class="algolia-alternative-product__placeholder" aria-hidden="true"></span>
      </div>`;
  }

  /**
   * Escapes a value for safe interpolation into an HTML string.
   *
   * @param {unknown} value
   * @returns {string}
   */
  static escapeHtml(value) {
    const stringValue = value == null ? '' : String(value);
    return stringValue
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Renders the resolved alternative product (image, title and price) as a linked card.
   *
   * The price block reuses createPriceElement with a hit-like object so the B2B/B2C price
   * structure matches the rest of the search results. A placeholder image is used when the
   * product has no featured image.
   *
   * @param {{
   *   id: string,
   *   title: string,
   *   url: string | null,
   *   imageUrl: string | null,
   *   imageAlt: string,
   *   price: number,
   *   compareAtPrice: number
   * } | null | undefined} product - The first available alternative product, if any.
   * @returns {string} The alternative product HTML, or an empty string when unavailable.
   */
  static renderAlternativeProduct(product) {
    if (!product) return '';

    const title = AlgoliaTemplate.escapeHtml(product.title);
    const url = AlgoliaTemplate.escapeHtml(product.url || '#');
    const placeholder = window.theme?.assets?.placeholder || '';
    const baseImageUrl = product.imageUrl
      ? `${product.imageUrl}${product.imageUrl.includes('?') ? '&' : '?'}width=200`
      : placeholder;
    const imageUrl = AlgoliaTemplate.escapeHtml(baseImageUrl);
    const placeholderSrc = AlgoliaTemplate.escapeHtml(placeholder);
    const imageAlt = AlgoliaTemplate.escapeHtml(product.imageAlt || product.title);
    const label = window.algoliaAlternativeProductConfig?.alternativeProductLabel || '';

    const priceHit = {
      objectID: product.id,
      price: product.price || 0,
      compare_at_price: product.compareAtPrice || 0,
    };

    return `
      <a class="algolia-alternative-product__card" href="${url}">
        <img
          class="algolia-alternative-product__image"
          src="${imageUrl}"
          alt="${imageAlt}"
          loading="lazy"
          width="80"
          height="80"
          onerror="this.onerror=null;this.src='${placeholderSrc}'"
        >
        <div class="algolia-alternative-product__details">
          ${
            label
              ? `<div class="algolia-alternative-product__label">${AlgoliaTemplate.escapeHtml(
                  label
                )}</div>`
              : ''
          }
          <strong class="algolia-alternative-product__title">${title}</strong>
          ${window.algoliaAlternativeProductConfig?.showPrice ? AlgoliaTemplate.createPriceElement(priceHit) : ''}
        </div>
      </a>`;
  }
}
window.AlgoliaTemplate = AlgoliaTemplate;
export class AlgoliaDataUtils {
  /**
   * Cache for already resolved Shopify GIDs.
   * Stores the resolved file/image URL for each GID.
   *
   * @type {Map<string, string>}
   */
  static gidCache = new Map();

  /**
   * Debounce timer used to batch DOM-based GID resolution.
   *
   * @type {number | null}
   */
  static gidResolutionTimer = null;

  /**
   * Delay in milliseconds before batched GID resolution is executed.
   *
   * @type {number}
   */
  static GID_RESOLUTION_DELAY_MS = 50;

  /**
   * Cache for already resolved alternative product data, keyed by product GID.
   * Unresolved GIDs are cached as null to avoid refetching.
   *
   * @type {Map<string, {
   *   id: string,
   *   title: string,
   *   handle: string,
   *   url: string | null,
   *   available: boolean,
   *   imageUrl: string | null,
   *   imageAlt: string,
   *   price: number,
   *   compareAtPrice: number
   * } | null>}
   */
  static alternativeProductCache = new Map();

  /**
   * Debounce timer used to batch alternative product GID resolution.
   *
   * @type {number | null}
   */
  static alternativeGidResolutionTimer = null;

  static energyLabelByHandle = new Map();

  /**
   * Checks whether a given value is a Shopify GID.
   *
   * @param {unknown} value - The value to validate.
   * @returns {boolean} Returns true if the value is a Shopify GID, otherwise false.
   */
  static isShopifyGid(value) {
    return typeof value === 'string' && value.startsWith('gid://shopify/');
  }

  /**
   * Extracts a URL from a metafield value.
   *
   * Supports:
   * - plain URL strings
   * - objects with a `url` property
   * - JSON strings containing a `url` property
   *
   * @param {unknown} value - The metafield value to parse.
   * @returns {string | null} The extracted URL or null if no valid URL could be found.
   */
  static parseMetafieldToUrl(value) {
    if (!value) return null;

    if (typeof value === 'object' && value.url) {
      return value.url;
    }

    if (typeof value === 'string' && value.startsWith('{')) {
      try {
        const parsed = JSON.parse(value);
        return parsed.url || null;
      } catch {
        return null;
      }
    }

    if (typeof value === 'string' && value.startsWith('http')) {
      return value;
    }

    return null;
  }

  /**
   * Extracts normalized metafield data from a metafield value.
   *
   * If the value is a Shopify GID, the GID is returned and a cached URL is used if available.
   * Otherwise the method attempts to extract a direct URL from the value.
   *
   * @param {unknown} value - The metafield value to inspect.
   * @returns {{ url: string | null, gid: string | null }} Normalized metafield data.
   */
  static extractMetafieldData(value) {
    const result = { url: null, gid: null };

    if (AlgoliaDataUtils.isShopifyGid(value)) {
      result.gid = value;
      if (AlgoliaDataUtils.gidCache.has(value)) {
        result.url = AlgoliaDataUtils.gidCache.get(value);
      }
    } else {
      result.url = AlgoliaDataUtils.parseMetafieldToUrl(value);
    }

    return result;
  }

  /**
   * Resolves multiple Shopify GIDs in a single Storefront API request.
   *
   * Only uncached GIDs are requested. Cached values are reused automatically.
   *
   * @param {string[]} gids - Array of Shopify GIDs to resolve.
   * @returns {Promise<Map<string, string | null>>} A map of GID to resolved URL or null.
   */
  static async resolveGidsBatch(gids) {
    if (!gids || gids.length === 0) return new Map();

    const uniqueGids = [...new Set(gids)];
    const uncachedGids = uniqueGids.filter((gid) => !AlgoliaDataUtils.gidCache.has(gid));

    if (uncachedGids.length === 0) {
      return new Map(uniqueGids.map((gid) => [gid, AlgoliaDataUtils.gidCache.get(gid)]));
    }

    const storefrontToken = window.storefrontAccessToken;
    if (!storefrontToken) {
      AlgoliaUtils?.logError?.(
        new Error('Storefront Access Token not configured'),
        'resolveGidsBatch'
      );
      return new Map();
    }

    const query = `
      query getFileUrls($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on MediaImage {
            id
            image { url }
          }
          ... on GenericFile {
            id
            url
          }
        }
      }
    `;

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopRoot}api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({ query, variables: { ids: uncachedGids } }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const nodes = data?.data?.nodes || [];

      for (const node of nodes) {
        if (node?.id) {
          const url = node.image?.url || node.url;
          if (url) {
            AlgoliaDataUtils.gidCache.set(node.id, url);
          }
        }
      }

      uncachedGids.forEach((gid) => {
        if (!AlgoliaDataUtils.gidCache.has(gid)) {
          AlgoliaDataUtils.gidCache.set(gid, null);
        }
      });

      return new Map(uniqueGids.map((gid) => [gid, AlgoliaDataUtils.gidCache.get(gid) || null]));
    } catch (error) {
      AlgoliaUtils?.logError?.(error, 'resolveGidsBatch API call');
      return new Map();
    }
  }

  /**
   * Resolves all pending energy-label-related GIDs currently present in the DOM.
   *
   * This method uses debouncing to avoid repeated API calls while multiple product cards
   * are being rendered. After resolution, matching DOM elements are updated in place.
   *
   * @returns {void}
   */
  static applyEnergyLabelUrl(el, url, kind) {
    if (!el || !url) return;
    if (kind === 'label') {
      if (el.tagName === 'BUTTON') el.setAttribute('data-src', url);
      else el.setAttribute('href', url);
      el.closest('fancy-box-component')?.setAttribute('data-src', url);
      el.removeAttribute('data-energy-label-gid');
    } else {
      el.setAttribute('href', url);
      el.removeAttribute('data-energy-spec-gid');
    }
  }

  static async fetchEnergyLabelByHandle(handle) {
    if (!handle) return null;
    if (AlgoliaDataUtils.energyLabelByHandle.has(handle)) {
      return AlgoliaDataUtils.energyLabelByHandle.get(handle);
    }

    const request = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const base = root.endsWith('/') ? root : `${root}/`;
        const response = await fetch(
          `${base}products/${encodeURIComponent(handle)}?sections=algolia-energy-label-data`,
          { credentials: 'same-origin' }
        );
        if (!response.ok) {
          AlgoliaDataUtils.energyLabelByHandle.set(handle, null);
          return null;
        }
        const html = (await response.json())?.['algolia-energy-label-data'] || '';
        const payload = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('algolia-energy-label-payload')?.textContent;
        const parsed = payload ? JSON.parse(payload) : null;
        AlgoliaDataUtils.energyLabelByHandle.set(handle, parsed);
        return parsed;
      } catch (error) {
        AlgoliaUtils.logError(error, 'fetchEnergyLabelByHandle');
        AlgoliaDataUtils.energyLabelByHandle.set(handle, null);
        return null;
      }
    })();

    AlgoliaDataUtils.energyLabelByHandle.set(handle, request);
    return request;
  }

  static resolveEnergyLabelGids() {
    if (AlgoliaDataUtils.gidResolutionTimer) {
      clearTimeout(AlgoliaDataUtils.gidResolutionTimer);
    }

    AlgoliaDataUtils.gidResolutionTimer = setTimeout(async () => {
      AlgoliaDataUtils.gidResolutionTimer = null;

      const gids = [...document.querySelectorAll('[data-energy-label-gid], [data-energy-spec-gid]')]
        .map(
          (el) =>
            el.getAttribute('data-energy-label-gid') || el.getAttribute('data-energy-spec-gid')
        )
        .filter(Boolean);

      if (gids.length) await AlgoliaDataUtils.resolveGidsBatch(gids);

      document.querySelectorAll('[data-energy-label-gid]').forEach((el) => {
        const url = AlgoliaDataUtils.gidCache.get(el.getAttribute('data-energy-label-gid'));
        if (url) AlgoliaDataUtils.applyEnergyLabelUrl(el, url, 'label');
      });
      document.querySelectorAll('[data-energy-spec-gid]').forEach((el) => {
        const url = AlgoliaDataUtils.gidCache.get(el.getAttribute('data-energy-spec-gid'));
        if (url) AlgoliaDataUtils.applyEnergyLabelUrl(el, url, 'spec');
      });

      const pending = [
        ...document.querySelectorAll('[data-energy-label-gid], [data-energy-spec-gid]'),
      ];
      if (!pending.length) return;

      const handles = [
        ...new Set(pending.map((el) => el.getAttribute('data-product-handle')).filter(Boolean)),
      ];
      await Promise.all(handles.map((handle) => AlgoliaDataUtils.fetchEnergyLabelByHandle(handle)));

      document.querySelectorAll('[data-energy-label-gid], [data-energy-spec-gid]').forEach((el) => {
        const data = AlgoliaDataUtils.energyLabelByHandle.get(
          el.getAttribute('data-product-handle')
        );
        if (!data || typeof data.then === 'function') return;
        if (el.hasAttribute('data-energy-label-gid') && data.labelUrl) {
          AlgoliaDataUtils.applyEnergyLabelUrl(el, data.labelUrl, 'label');
        }
        if (el.hasAttribute('data-energy-spec-gid') && data.dataSheetUrl) {
          AlgoliaDataUtils.applyEnergyLabelUrl(el, data.dataSheetUrl, 'spec');
        }
      });
    }, AlgoliaDataUtils.GID_RESOLUTION_DELAY_MS);
  }

  /**
   * Normalizes an alternative-products metafield value into a list of Shopify product GIDs.
   *
   * Accepts arrays, JSON-encoded arrays, comma-separated strings, single GIDs, or arrays of
   * objects exposing an `id`/`_id`. Non-GID entries are discarded.
   *
   * @param {unknown} value - The raw metafield value or DOM attribute string.
   * @returns {string[]} Array of valid Shopify product GIDs.
   */
  static parseGidList(value) {
    if (value == null) return [];

    /** @type {unknown[]} */
    let list;

    if (Array.isArray(value)) {
      list = value;
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          list = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          list = trimmed.split(',');
        }
      } else {
        list = trimmed.split(',');
      }
    } else {
      list = [value];
    }

    return list
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          const record = /** @type {Record<string, unknown>} */ (item);
          const id = record.id || record._id;
          return typeof id === 'string' ? id : '';
        }
        return '';
      })
      .filter((gid) => AlgoliaDataUtils.isShopifyGid(gid));
  }

  /**
   * Resolves multiple Shopify product GIDs in a single Storefront API request.
   *
   * Only uncached GIDs are requested; unresolved GIDs are cached as null to prevent refetching.
   *
   * @param {string[]} gids - Array of Shopify product GIDs to resolve.
   * @returns {Promise<void>}
   */
  static async resolveProductGidsBatch(gids) {
    if (!gids || gids.length === 0) return;

    const uniqueGids = [...new Set(gids)];
    const uncachedGids = uniqueGids.filter(
      (gid) => !AlgoliaDataUtils.alternativeProductCache.has(gid)
    );

    if (uncachedGids.length === 0) return;

    const storefrontToken = window.storefrontAccessToken;
    if (!storefrontToken) {
      AlgoliaUtils?.logError?.(
        new Error('Storefront Access Token not configured'),
        'resolveProductGidsBatch'
      );
      return;
    }

    const query = `
      query getAlternativeProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            onlineStoreUrl
            availableForSale
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    `;

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopRoot}api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({ query, variables: { ids: uncachedGids } }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const nodes = data?.data?.nodes || [];
      for (const node of nodes) {
        if (node?.id) {
          AlgoliaDataUtils.alternativeProductCache.set(node.id, {
            id: node.id,
            title: node.title || '',
            handle: node.handle || '',
            url:
              node.onlineStoreUrl ||
              (node.handle ? `${AlgoliaTemplate.prefix}products/${node.handle}` : null),
            available: node.availableForSale === true,
            imageUrl: node.featuredImage?.url || null,
            imageAlt: node.featuredImage?.altText || node.title || '',
            price: node.priceRange?.minVariantPrice?.amount
              ? parseFloat(node.priceRange.minVariantPrice.amount)
              : 0,
            compareAtPrice: node.compareAtPriceRange?.minVariantPrice?.amount
              ? parseFloat(node.compareAtPriceRange.minVariantPrice.amount)
              : 0,
          });
        }
      }

      // Cache misses as null so they are not requested again.
      uncachedGids.forEach((gid) => {
        if (!AlgoliaDataUtils.alternativeProductCache.has(gid)) {
          AlgoliaDataUtils.alternativeProductCache.set(gid, null);
        }
      });

      // Surface GIDs that did not resolve to a Storefront product. The most common cause is
      // that the product isn't published to the sales channel tied to the Storefront token.
      const unresolved = uncachedGids.filter(
        (gid) => AlgoliaDataUtils.alternativeProductCache.get(gid) == null
      );
      if (unresolved.length > 0) {
        console.warn(
          '[alternative-product] These GIDs did not resolve via the Storefront API ' +
            '(check sales-channel publication / token scope):',
          unresolved
        );
      }
    } catch (error) {
      AlgoliaUtils?.logError?.(error, 'resolveProductGidsBatch API call');
    }
  }

  /**
   * Resolves all pending alternative-product placeholders currently present in the DOM.
   *
   * Uses debouncing to coalesce multiple rendered cards into a single batched API request.
   * After resolution, each placeholder is replaced with the first available alternative
   * product (image + title), or emptied when none is available.
   *
   * @returns {void}
   */
  static resolveAlternativeProductGids() {
    if (AlgoliaDataUtils.alternativeGidResolutionTimer) {
      clearTimeout(AlgoliaDataUtils.alternativeGidResolutionTimer);
    }

    AlgoliaDataUtils.alternativeGidResolutionTimer = setTimeout(async () => {
      AlgoliaDataUtils.alternativeGidResolutionTimer = null;

      // Collect the GIDs that still need resolving for the batch request.
      const allGids = new Set();
      document.querySelectorAll('[data-alternative-product-gids]').forEach((el) => {
        AlgoliaDataUtils.parseGidList(el.getAttribute('data-alternative-product-gids')).forEach(
          (gid) => allGids.add(gid)
        );
      });

      if (allGids.size === 0) return;

      await AlgoliaDataUtils.resolveProductGidsBatch([...allGids]);

      // Re-query AFTER awaiting: InstantSearch may have re-rendered during the request, so the
      // NodeList captured before the await can point at detached nodes. Re-querying ensures we
      // only patch placeholders that are still in the DOM and still pending.
      document.querySelectorAll('[data-alternative-product-gids]').forEach((el) => {
        const gids = AlgoliaDataUtils.parseGidList(
          el.getAttribute('data-alternative-product-gids')
        );
        if (gids.length === 0) return;

        const resolved = gids.map((gid) => AlgoliaDataUtils.alternativeProductCache.get(gid));
        const firstAvailable = resolved.find((product) => product && product.available);
        const allResolved = gids.every((gid) => AlgoliaDataUtils.alternativeProductCache.has(gid));

        if (firstAvailable) {
          el.innerHTML = AlgoliaTemplate.renderAlternativeProduct(firstAvailable);
          el.classList.remove('is-loading', 'is-empty');
          el.removeAttribute('data-alternative-product-gids');
        } else if (allResolved) {
          // Every alternative resolved and none is available → genuinely empty.
          el.innerHTML = '';
          el.classList.remove('is-loading');
          el.classList.add('is-empty');
          el.removeAttribute('data-alternative-product-gids');
        }
        // Otherwise some GIDs are still unresolved (e.g. a concurrent render): leave the
        // placeholder in its loading state so a later pass can complete it.
      });
    }, AlgoliaDataUtils.GID_RESOLUTION_DELAY_MS);
  }

  /**
   * Retrieves customer data from the current `window.dataLayer`.
   *
   * If no entry with `user_data` exists, a default object with null values is returned.
   *
   * @returns {{
   *   first_name: string | null,
   *   last_name: string | null,
   *   email: string | null,
   *   phone: string | null,
   *   city: string | null,
   *   country: string | null,
   *   zip: string | null,
   *   region: string | null,
   *   street: string | null,
   *   customer_id: string | number | null
   * }} Customer data object.
   */
  static getCustomerData() {
    const existingDataLayer = window.dataLayer || [];

    for (let i = 0; i < existingDataLayer.length; i += 1) {
      if (existingDataLayer[i].user_data) {
        return existingDataLayer[i].user_data;
      }
    }

    return {
      first_name: null,
      last_name: null,
      email: null,
      phone: null,
      city: null,
      country: null,
      zip: null,
      region: null,
      street: null,
      customer_id: null,
    };
  }

  /**
   * Builds normalized energy label data for a given Algolia hit.
   *
   * @param {object} hit
   * @returns {{
   *   labelMobile: string,
   *   labelDesktop: string,
   *   energyLabel: string,
   *   dataSheetUrl: string | null,
   *   energyLabelExtendedUrl: string | null,
   *   dataSheetGid: string | null,
   *   energyLabelGid: string | null,
   *   productHandle: string
   * } | null} Normalized energy label data or null if no valid data is available.
   */
  static getEnergyLabelData(hit) {
    const energyLabelsMap = AlgoliaTemplate.ENERGY_LABELS_MAP;
    try {
      const energyMetafield = hit.meta?.custom?.energy;
      const energyLabel = hit.meta?.custom?.energyefficiency_label;
      const energyClass = String(hit.meta?.custom?.energieeffizienzklasse || '').trim();

      if ((!energyMetafield && !energyLabel && !energyClass) || !energyLabelsMap) {
        return null;
      }

      let energyLabelId;
      try {
        energyLabelId = (energyMetafield._id || energyMetafield).split('/').pop();
      } catch {
        energyLabelId = null;
      }

      let energyData =
        (energyLabelId && energyLabelsMap[energyLabelId]) || energyLabelsMap[energyClass] || null;

      if (!energyData) {
        energyData = Object.values(energyLabelsMap).findLast((label) => {
          const suffix = String(label.energy_label || '')
            .trim()
            .split(/\s+/)
            .pop();
          return suffix === energyClass || suffix === energyLabel;
        });
      }
      if (!energyData) return null;

      const specData = AlgoliaDataUtils.extractMetafieldData(
        hit.meta?.custom?.energyefficiency_specification
      );
      const labelData = AlgoliaDataUtils.extractMetafieldData(energyLabel);

      return {
        labelMobile: energyData.label_mobile,
        labelDesktop: energyData.label_desktop,
        energyLabel: energyData.energy_label,
        dataSheetUrl: specData.url || null,
        energyLabelExtendedUrl: labelData.url || null,
        dataSheetGid: specData.gid && !specData.url ? specData.gid : null,
        energyLabelGid: labelData.gid && !labelData.url ? labelData.gid : null,
        productHandle: hit.handle,
      };
    } catch {
      return null;
    }
  }
}
// Initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    AlgoliaTemplate.initBadgeMap();
    if (document.getElementById('algolia-vendor-logos')) {
      AlgoliaTemplate.loadVendorLogosMap();
    }
  });
} else {
  AlgoliaTemplate.initBadgeMap();
  if (document.getElementById('algolia-vendor-logos')) {
    AlgoliaTemplate.loadVendorLogosMap();
  }
}
